import { inject, Injectable } from '@angular/core';
import { UsersHttpService } from '@core/http/users.http.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AnalyticsService } from '@core/services/analytics.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { parseError } from '@state/errors.parser';
import { combineLatest, of, timer } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';
import * as usersActions from './users.actions';
import { environment } from '@environment';
import { DataListingRequestsHttpService } from '@core/http/data-listing-requests.http.service';

@Injectable()
export class UsersEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(UsersHttpService);
  private readonly dataListingRequestsHttpService = inject(DataListingRequestsHttpService);
  private readonly snackService = inject(StackedMessagesService);
  private readonly analyticsService = inject(AnalyticsService);

  searchForeigners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.searchForeigners),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.http.searchForeigners(rest).pipe(
          map(foreigners => {
            return usersActions.getUsersSuccess({ users: foreigners });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.searchForeigners.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.getUsersError({ errorMessage }));
          })
        );
      })
    )
  );

  getEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.getEmployees),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.http.searchEmployees(rest).pipe(
          map(employees => {
            return usersActions.getUsersSuccess({ users: employees });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.getEmployees.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.getUsersError({ errorMessage }));
          })
        );
      })
    )
  );

  getCompanyEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.getCompanyEmployees),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.http.searchMyCompanyEmployees(rest).pipe(
          map(companyEmployees => {
            return usersActions.getUsersSuccess({ users: companyEmployees });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.getCompanyEmployees.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.getUsersError({ errorMessage }));
          })
        );
      })
    )
  );

  getUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.getUsers),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.http.searchUsers(rest).pipe(
          map(users => {
            return usersActions.getUsersSuccess({ users });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.getUsers.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.getUsersError({ errorMessage }));
          })
        );
      })
    )
  );

  getForeignerBasicData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.getForeignerBasicData),
      switchMap(({ foreignerId }) => {
        return this.http.getForeignerBasicData(foreignerId).pipe(
          map(foreigner => {
            return usersActions.getForeignerBasicDataSuccess({ foreigner });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.getForeignerBasicData.type);
            return of(usersActions.getForeignerBasicDataError({ errorMessage }));
          })
        );
      })
    )
  );

  getEmployeeBasicData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.getEmployeeBasicData),
      switchMap(({ employeeId }) => {
        return this.http.getEmployeeBasicData(employeeId).pipe(
          map(employee => {
            return usersActions.getEmployeeBasicDataSuccess({ employee });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.getForeignerBasicData.type);
            return of(usersActions.getEmployeeBasicDataError({ errorMessage }));
          })
        );
      })
    )
  );

  selfRegister$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.selfRegister),
      delay(100),
      switchMap(({ opts }) => {
        return this.http.selfRegister(opts).pipe(
          map(() => {
            this.analyticsService.trackEvent('user_event', 'user_created_new_account');
            return usersActions.selfRegisterSuccess();
          }),
          catchError(error => {
            let errorMessage = error?.error?.message;
            if (!errorMessage) {
              errorMessage = parseError(error, usersActions.getUsers.type);
            }
            return of(usersActions.selfRegisterError({ errorMessage }));
          })
        );
      })
    )
  );

  createForeigner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.createForeigner),
      switchMap(({ payload }) => {
        return this.http.createForeigner(payload).pipe(
          map(({ user, emailSent }) => {
            if (emailSent) {
              this.snackService.showInfo({ message: 'COMMON.NOTIFICATION.ACTIVATION_EMAIL_SENT' });
            } else {
              this.snackService.showError({
                message: 'MANAGEMENT.FOREIGNERS.ACCOUNT-CREATED-BUT-EMAIL-SENDING-FAILED',
              });
            }
            return usersActions.createUserSuccess({ createdUser: user });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.createForeigner.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.createUserError({ errorMessage }));
          })
        );
      })
    )
  );

  createEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.createEmployee),
      switchMap(({ payload }) => {
        return this.http.createEmployee(payload).pipe(
          map(({ user, emailSent }) => {
            if (emailSent) {
              this.snackService.showInfo({ message: 'COMMON.NOTIFICATION.ACTIVATION_EMAIL_SENT' });
            } else {
              this.snackService.showError({ message: 'MANAGEMENT.EMPLOYEES.ACCOUNT-CREATED-BUT-EMAIL-SENDING-FAILED' });
            }
            return usersActions.createUserSuccess({ createdUser: user });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.createEmployee.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.createUserError({ errorMessage }));
          })
        );
      })
    )
  );

  updateEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.updateEmployee),
      switchMap(({ payload, userId }) => {
        return this.http.updateEmployee(payload, userId).pipe(
          map(updatedUser => {
            this.snackService.showInfo({ message: 'MANAGEMENT.USER_UPDATE_SUCCESS' });
            return usersActions.updateUserSuccess({ updatedUser });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.updateEmployee.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.updateUserError({ errorMessage }));
          })
        );
      })
    )
  );

  updateForeigner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.updateForeigner),
      switchMap(({ payload, userId }) => {
        return this.http.updateForeigner(payload, userId).pipe(
          map(updatedUser => {
            this.snackService.showInfo({ message: 'MANAGEMENT.USER_UPDATE_SUCCESS' });
            return usersActions.updateUserSuccess({ updatedUser });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.updateForeigner.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.updateUserError({ errorMessage }));
          })
        );
      })
    )
  );

  removeForeigner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.removeForeigner),
      switchMap(({ userId }) => {
        return this.http.removeForeigner(userId).pipe(
          map(({ success }) => {
            if (success) {
              this.snackService.showInfo({ message: 'MANAGEMENT.FOREIGNERS.FOREIGNER-REMOVED' });
              return usersActions.removeUserSuccess({ removedUserId: userId });
            } else {
              this.snackService.showError({ message: 'MANAGEMENT.FOREIGNERS.ERROR-REMOVING-FOREIGNER' });
              return usersActions.removeUserError({ errorMessage: 'FOREIGNERS.ERROR-REMOVING-FOREIGNER' });
            }
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.removeForeigner.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.removeUserError({ errorMessage }));
          })
        );
      })
    )
  );

  restoreUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.restoreUser),
      switchMap(({ userId }) => {
        return this.http.restoreUser(userId).pipe(
          map(restoredUser => {
            return usersActions.restoreUserSuccess({ restoredUser });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.restoreUser.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.restoreUserError({ errorMessage }));
          })
        );
      })
    )
  );

  restoreForeigner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.restoreForeigner),
      switchMap(({ foreignerId }) => {
        return this.http.restoreForeigner(foreignerId).pipe(
          map(restoredUser => {
            return usersActions.restoreUserSuccess({ restoredUser });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.restoreForeigner.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.restoreUserError({ errorMessage }));
          })
        );
      })
    )
  );

  removeEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.removeEmployee),
      switchMap(({ userId }) => {
        return this.http.removeUser(userId).pipe(
          map(() => {
            this.snackService.showInfo({ message: 'MANAGEMENT.EMPLOYEES.EMPLOYEE-REMOVED' });
            return usersActions.removeUserSuccess({ removedUserId: userId });
          }),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.removeEmployee.type);
            this.snackService.showError({ message: errorMessage });
            return of(usersActions.removeUserError({ errorMessage }));
          })
        );
      })
    )
  );

  resendForeignerActivation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.resendForeignerActivation),
      switchMap(({ email, userId }) => {
        return this.http.resendForeignerActivation(email, userId).pipe(
          map(({ emailSent }) => usersActions.resendActivationSuccess({ emailSent })),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.resendForeignerActivation.type);
            return of(usersActions.resendActivationError({ errorMessage }));
          })
        );
      })
    )
  );

  resendEmployeeActivation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.resendEmployeeActivation),
      switchMap(({ email, userId }) => {
        return this.http.resendEmployeeActivation(email, userId).pipe(
          map(({ emailSent }) => usersActions.resendActivationSuccess({ emailSent })),
          catchError(error => {
            const errorMessage = parseError(error, usersActions.resendEmployeeActivation.type);
            return of(usersActions.resendActivationError({ errorMessage }));
          })
        );
      })
    )
  );

  getEmployeeTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.getEmployeeTypes),
      switchMap(() => {
        return this.http.getEmployeeTypes().pipe(
          map(roles => usersActions.getEmployeeTypesSuccess({ employeeTypes: roles })),
          catchError(() => {
            this.snackService.showError({ message: 'ERROR_FETCHING_EMPLOYEE_TYPES' });
            return of(usersActions.getEmployeeTypesError());
          })
        );
      })
    )
  );

  getEAllRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.getAllRoles),
      switchMap(() => {
        return this.http.getAllRoles().pipe(
          map(roles => usersActions.getAllRolesSuccess({ allRoles: roles })),
          catchError(() => {
            this.snackService.showError({ message: 'ERROR_FETCHING_EMPLOYEE_TYPES' });
            return of(usersActions.getAllRolesError());
          })
        );
      })
    )
  );

  reassignForeigners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.reassignForeigners),
      switchMap(({ opts }) => {
        return this.http.reassignForeigners(opts).pipe(
          delay(1000),
          map(() => usersActions.reassignForeignersSuccess()),
          catchError(() => {
            this.snackService.showError({ message: 'Błąd przepisywania obcokrajowców!' });
            return of(usersActions.reassignForeignersError());
          })
        );
      })
    )
  );

  modifyUsersTag = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.modifyUsersTags),
      switchMap(({ payload }) => {
        const request$ = this.http.modifyUsersTags(payload);
        const delay$ = timer((environment.minLoadingDataTime || 0.5) * 1000);
        return combineLatest([request$, delay$]).pipe(
          map(([{ usersTags }]) => usersActions.modifyUsersTagsSuccess({ usersTags })),
          catchError(({ error }) => {
            this.snackService.showError({ message: 'COMMON.ERROR.ERROR_ADDING_USERS_TAGS' });
            const errorMessage = parseError(error, usersActions.modifyUsersTags.type);
            return of(usersActions.modifyUsersTagsError({ errorMessage }));
          })
        );
      })
    )
  );

  getAllRequestsDataListing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.getAllRequestsDataListing),
      switchMap(() => {
        return this.dataListingRequestsHttpService.getAllRequestsDataListing().pipe(
          map(dataListingRequests => usersActions.getAllRequestsDataListingSuccess({ dataListingRequests })),
          catchError(() => {
            return of(usersActions.getAllRequestsDataListingError());
          })
        );
      })
    )
  );

  getDataListingRequestsOfUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.getDataListingRequestsOfUser),
      switchMap(({ payload }) => {
        return this.dataListingRequestsHttpService.getDataListingRequestsOfUser(payload).pipe(
          map(dataListingRequests => usersActions.getDataListingRequestsOfUserSuccess({ dataListingRequests })),
          catchError(() => {
            return of(usersActions.getDataListingRequestsOfUserError());
          })
        );
      })
    )
  );

  requestDataListing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.requestDataListing),
      switchMap(() => {
        const request$ = this.dataListingRequestsHttpService.requestDataListing();
        const delay$ = timer((environment.minLoadingDataTime || 0.5) * 1000);
        return combineLatest([request$, delay$]).pipe(
          map(([dataListingRequest]) => usersActions.requestDataListingSuccess({ dataListingRequest })),
          catchError(() => {
            return of(usersActions.requestDataListingError());
          })
        );
      })
    )
  );

  changeDataListingRequestStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.changeDataListingRequestStatus),
      switchMap(({ payload }) => {
        return this.dataListingRequestsHttpService.changeDataListingRequestStatus(payload).pipe(
          map(dataListingRequest => usersActions.changeDataListingRequestStatusSuccess({ dataListingRequest })),
          catchError(() => {
            return of(usersActions.changeDataListingRequestStatusError());
          })
        );
      })
    )
  );

  getUserConsents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(usersActions.getUserConsents),
      switchMap(() => {
        return this.http.getUserConsents().pipe(
          map(userConsents => usersActions.getUserConsentsSuccess({ userConsents })),
          catchError(({ error }) => {
            const errorMessage = parseError(error, usersActions.getUserConsents.type);
            return of(usersActions.getUserConsentsError({ errorMessage }));
          })
        );
      })
    )
  );
}
